import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import api from '../../../../services/httpClient';
import { PROFILE_ENDPOINTS } from '../../../../services/endpoints/rec/profile.endpoints';
import Button from '../../../common/Button';
import Dialog from '../../../common/Dialog';
import Loading from '../../../common/Loading';
import StatusChip from '../../../common/StatusChip';
import ProfileIcon from '../../../common/icons/profile';
import { formatDate } from '../../../../utils/date';

interface ProfileDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  profileId: number;
}

interface ProfileData {
  basic: {
    id: number;
    name: string;
    designation: string;
    about: string;
    email: string;
    phoneNumber: string;
    profileUrl: string;
    updatedAt: string;
    addedBy: number;
  };
  skills: {
    id: number;
    name: string;
  }[];
  educations: {
    degree: string;
    institute: string;
    duration: string;
    startDate: string;
    endDate: string;
  }[];
  employment: {
    id: number;
    company: string;
    duration: string;
    latestPosition: string;
  }[];
  vacancy: {
    id: number;
    vacancy: string;
  }[];
}

const ProfileDetailsPopup: React.FC<ProfileDetailsPopupProps> = ({
  open,
  onClose,
  profileId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  useEffect(() => {
    if (!open || !profileId) return;
    const fetchProfileDetails = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<{ data: ProfileData }>(PROFILE_ENDPOINTS.byId(profileId), {
          headers: { ...API_HEADERS.LINKEDIN_PROFILE_READ },
        });
        setProfileData(response.data.data);
      } catch (error) {
        console.error('Error fetching profile details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileDetails();
  }, [open, profileId]);

  if (!profileData || isLoading) {
    return (
      <Dialog open={open} onClose={onClose}>
        <Box sx={{ position: 'relative', p: 3 }}>
          {isLoading ? (
            <Loading message="Loading profile details..." />
          ) : (
            <Typography>No profile data available</Typography>
          )}
        </Box>
      </Dialog>
    );
  }

  const profileUrl = profileData.basic.profileUrl?.startsWith('http') ? profileData.basic.profileUrl : `https://${profileData.basic.profileUrl}`;


  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ position: 'relative', p: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: '#6c5ce7',
                fontSize: '24px',
              }}
            >
              <ProfileIcon />
            </Avatar>
            <Box sx={{ ml: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '24px' }}>
                {profileData.basic.name}
              </Typography>
              <Typography  color="#00000066" sx={{ fontSize: '16px', fontWeight: 500 }}>
                {profileData.basic.designation}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Vacancies</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profileData.vacancy.map((vacancy) => (
                <StatusChip key={vacancy.id} label={vacancy.vacancy} className='view-tag-vacancy' />
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>About</Typography>
            <Typography sx={{ lineHeight: "100%", fontSize: '15px', fontWeight: 500 }}>
              {profileData.basic.about}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>Skills and Technologies</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profileData.skills.map((skill) => (
                <StatusChip key={skill.name} label={skill.name} className='view-tag' />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', mb: 4, gap: 8 }}>
            <Box>
              <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>Email</Typography>
              <Typography  color='#000000' sx={{ fontSize: '15px', fontWeight: 500 }}>{profileData.basic.email || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>Phone</Typography>
              <Typography  color='#000000' sx={{ fontSize: '15px', fontWeight: 500 }}>{profileData.basic.phoneNumber || 'N/A'}</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Education</Typography>
            {profileData.educations.map((edu, index) => (
              <Box
                key={index}
                sx={{ backgroundColor: '#F1F3FF', p: 2, borderRadius: 1, mb: 1 }}
              >
                <Typography color='#000000' sx={{ fontSize: '15px', fontWeight: 500 }}>{edu.degree} - {edu.institute}</Typography>
                <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>
                  {edu.duration}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1, lineHeight: "100%", fontSize: '19px', fontWeight: 600 }}>Work Experience</Typography>
            {profileData.employment.map((exp, index) => (
              <Box
                key={index}
                sx={{ backgroundColor: '#F1F3FF', p: 2, borderRadius: 1, mb: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Box>
                    <Typography color='#000000' sx={{ fontSize: '15px', fontWeight: 500 }}>{exp.latestPosition} - {exp.company}</Typography>
                    <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>
                      {exp.duration}
                    </Typography>
                  </Box>
                  {/* <Box>
                    <Typography  color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>Skills/Technologies</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {exp.skills.map((skill) => (
                        <StyledChip key={skill} label={skill} />
                      ))}
                    </Box>
                  </Box> */}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 8 }}>
            <Box>
              <Typography  color='#00000066' sx={{ fontSize: '12px', fontWeight: 500 }}>LinkedIn Profile URL</Typography>
              <Typography
                component="a"
                href={profileUrl}
                target="_blank"
                sx={{ color: 'black' }}
                rel="noopener noreferrer"
              >
                {profileData.basic.profileUrl}
              </Typography>
            </Box>
            <Box>
              <Typography  color='#00000066' sx={{ fontSize: '13.5px', fontWeight: 500 }}>
                LinkedIn Profile Last Synced On
              </Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>{formatDate(profileData.basic.updatedAt)}</Typography>
            </Box>
          </Box>

        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button onClick={onClose} variant="outlined" width="120px" style={{ height: '25px' }}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProfileDetailsPopup; 